import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  orderBy,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { db } from '@/config/firebase.config';
import { ServiceOffering, CreateServiceOfferingData, UpdateServiceOfferingData } from '@/types';
import { COLLECTIONS } from '@/constants';
import { convertFirestoreDoc } from '@/utils';

/**
 * Create a new service offering
 */
export const createServiceOffering = async (
  data: CreateServiceOfferingData
): Promise<string> => {
  try {
    const offeringData = {
      ...data,
      isActive: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.SERVICE_OFFERINGS), offeringData);
    return docRef.id;
  } catch (error) {
    console.error('Error creating service offering:', error);
    throw error;
  }
};

/**
 * Get a service offering by ID
 */
export const getServiceOffering = async (offeringId: string): Promise<ServiceOffering | null> => {
  try {
    const offeringRef = doc(db, COLLECTIONS.SERVICE_OFFERINGS, offeringId);
    const offeringSnap = await getDoc(offeringRef);

    if (!offeringSnap.exists()) {
      return null;
    }

    return convertFirestoreDoc<ServiceOffering>({
      id: offeringSnap.id,
      ...offeringSnap.data(),
    });
  } catch (error) {
    console.error('Error getting service offering:', error);
    throw error;
  }
};

/**
 * Get all active service offerings (excluding current user's)
 */
export const getAvailableServiceOfferings = async (
  currentUserId?: string,
  limitCount: number = 50
): Promise<ServiceOffering[]> => {
  try {
    const offeringsRef = collection(db, COLLECTIONS.SERVICE_OFFERINGS);
    let q = query(
      offeringsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      firestoreLimit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    const offerings = querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceOffering>({
        id: doc.id,
        ...doc.data(),
      })
    );

    // Filter out current user's offerings on client side
    if (currentUserId) {
      return offerings.filter((offering) => offering.userId !== currentUserId);
    }

    return offerings;
  } catch (error) {
    console.error('Error getting available service offerings:', error);
    throw error;
  }
};

/**
 * Get service offerings by user ID
 */
export const getUserServiceOfferings = async (userId: string): Promise<ServiceOffering[]> => {
  try {
    const offeringsRef = collection(db, COLLECTIONS.SERVICE_OFFERINGS);
    const q = query(offeringsRef, where('userId', '==', userId), orderBy('createdAt', 'desc'));

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceOffering>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting user service offerings:', error);
    throw error;
  }
};

/**
 * Update a service offering
 */
export const updateServiceOffering = async (
  offeringId: string,
  data: UpdateServiceOfferingData
): Promise<void> => {
  try {
    const offeringRef = doc(db, COLLECTIONS.SERVICE_OFFERINGS, offeringId);

    await updateDoc(offeringRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error('Error updating service offering:', error);
    throw error;
  }
};

/**
 * Deactivate a service offering
 */
export const deactivateServiceOffering = async (offeringId: string): Promise<void> => {
  try {
    await updateServiceOffering(offeringId, { isActive: false });
  } catch (error) {
    console.error('Error deactivating service offering:', error);
    throw error;
  }
};

/**
 * Delete a service offering
 */
export const deleteServiceOffering = async (offeringId: string): Promise<void> => {
  try {
    const offeringRef = doc(db, COLLECTIONS.SERVICE_OFFERINGS, offeringId);
    await deleteDoc(offeringRef);
  } catch (error) {
    console.error('Error deleting service offering:', error);
    throw error;
  }
};

/**
 * Search service offerings by category
 */
export const searchServicesByCategory = async (
  category: string,
  currentUserId?: string
): Promise<ServiceOffering[]> => {
  try {
    const offeringsRef = collection(db, COLLECTIONS.SERVICE_OFFERINGS);
    const q = query(
      offeringsRef,
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    let offerings = querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ServiceOffering>({
        id: doc.id,
        ...doc.data(),
      })
    );

    // Filter by category and exclude current user
    offerings = offerings.filter((offering) => {
      const matchesCategory = offering.serviceTypes.some((typeId) =>
        typeId.startsWith(category)
      );
      const isNotCurrentUser = !currentUserId || offering.userId !== currentUserId;
      return matchesCategory && isNotCurrentUser;
    });

    return offerings;
  } catch (error) {
    console.error('Error searching services by category:', error);
    throw error;
  }
};

/**
 * Search service offerings by payment preference
 */
export const searchServicesByPayment = async (
  paymentPreference: string,
  currentUserId?: string
): Promise<ServiceOffering[]> => {
  try {
    const offerings = await getAvailableServiceOfferings(currentUserId);

    return offerings.filter((offering) =>
      offering.paymentPreferences.includes(paymentPreference as never)
    );
  } catch (error) {
    console.error('Error searching services by payment:', error);
    throw error;
  }
};
